import { Injectable } from '@angular/core';
import { PanelPosition } from '../services/ui-state.service';

export interface ComponentBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ViewportBounds {
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
}

@Injectable({
  providedIn: 'root',
})
export class PositionCalculatorService {
  private readonly INSPECTOR_WIDTH = 320;
  private readonly INSPECTOR_HEIGHT = 400;
  private readonly MARGIN = 16;

  calculateOptimalPosition(
    componentBounds: ComponentBounds,
    viewportBounds: ViewportBounds,
    inspectorWidth: number = this.INSPECTOR_WIDTH,
    inspectorHeight: number = this.INSPECTOR_HEIGHT
  ): PanelPosition {
    const positions = this.generatePossiblePositions(
      componentBounds,
      viewportBounds,
      inspectorWidth,
      inspectorHeight
    );

    // Pontua cada posição e retorna a melhor
    const scoredPositions = positions.map(position => ({
      position,
      score: this.scorePosition(position, componentBounds, viewportBounds),
    }));

    scoredPositions.sort((a, b) => b.score - a.score);

    return scoredPositions[0].position;
  }

  private generatePossiblePositions(
    componentBounds: ComponentBounds,
    viewportBounds: ViewportBounds,
    inspectorWidth: number,
    inspectorHeight: number
  ): PanelPosition[] {
    const positions: PanelPosition[] = [];

    // Lado direito do componente (preferido)
    positions.push({
      x: componentBounds.x + componentBounds.width + this.MARGIN,
      y: componentBounds.y,
      width: inspectorWidth,
      height: inspectorHeight,
    });

    // Lado esquerdo do componente
    positions.push({
      x: componentBounds.x - inspectorWidth - this.MARGIN,
      y: componentBounds.y,
      width: inspectorWidth,
      height: inspectorHeight,
    });

    // Abaixo do componente
    positions.push({
      x: componentBounds.x,
      y: componentBounds.y + componentBounds.height + this.MARGIN,
      width: inspectorWidth,
      height: inspectorHeight,
    });

    // Acima do componente
    positions.push({
      x: componentBounds.x,
      y: componentBounds.y - inspectorHeight - this.MARGIN,
      width: inspectorWidth,
      height: inspectorHeight,
    });

    // Centro da viewport (fallback)
    positions.push({
      x: (viewportBounds.width - inspectorWidth) / 2,
      y: (viewportBounds.height - inspectorHeight) / 2,
      width: inspectorWidth,
      height: inspectorHeight,
    });

    return positions;
  }

  private scorePosition(
    position: PanelPosition,
    componentBounds: ComponentBounds,
    viewportBounds: ViewportBounds
  ): number {
    let score = 100;

    // Penalidade por sair da viewport
    if (position.x < 0) score -= Math.abs(position.x);
    if (position.y < 0) score -= Math.abs(position.y);
    if (position.x + position.width > viewportBounds.width) {
      score -= (position.x + position.width - viewportBounds.width);
    }
    if (position.y + position.height > viewportBounds.height) {
      score -= (position.y + position.height - viewportBounds.height);
    }

    // Bônus por estar no lado direito (preferido)
    if (position.x > componentBounds.x + componentBounds.width) {
      score += 20;
    }

    // Bônus por alinhamento vertical com o componente
    const componentCenterY = componentBounds.y + componentBounds.height / 2;
    const inspectorCenterY = position.y + position.height / 2;
    const verticalAlignment = Math.abs(componentCenterY - inspectorCenterY);
    score -= verticalAlignment * 0.1;

    // Penalidade por sobreposição com o componente
    if (this.isOverlapping(position, componentBounds)) {
      score -= 50;
    }

    return score;
  }

  private isOverlapping(
    position: PanelPosition,
    componentBounds: ComponentBounds
  ): boolean {
    return !(
      position.x > componentBounds.x + componentBounds.width ||
      position.x + position.width < componentBounds.x ||
      position.y > componentBounds.y + componentBounds.height ||
      position.y + position.height < componentBounds.y
    );
  }

  adjustPositionToViewport(
    position: PanelPosition,
    viewportBounds: ViewportBounds
  ): PanelPosition {
    const adjusted = { ...position };

    // Ajusta posição horizontal
    if (adjusted.x < this.MARGIN) {
      adjusted.x = this.MARGIN;
    } else if (adjusted.x + adjusted.width > viewportBounds.width - this.MARGIN) {
      adjusted.x = viewportBounds.width - adjusted.width - this.MARGIN;
    }

    // Ajusta posição vertical
    if (adjusted.y < this.MARGIN) {
      adjusted.y = this.MARGIN;
    } else if (adjusted.y + adjusted.height > viewportBounds.height - this.MARGIN) {
      adjusted.y = viewportBounds.height - adjusted.height - this.MARGIN;
    }

    return adjusted;
  }

  getComponentBounds(element: HTMLElement): ComponentBounds {
    const rect = element.getBoundingClientRect();
    return {
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }

  getViewportBounds(): ViewportBounds {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    };
  }
}
